/**
 * Build-version poller.
 *
 * The PWA service worker uses Workbox autoUpdate + skipWaiting +
 * clientsClaim, which means a new SW activates on the next page load.
 * The catch: a tab that's been open for hours never reloads on its
 * own, so the user keeps seeing the old bundle until they manually
 * refresh. Field crews leave the app open for a whole shift; that
 * tab will never see a deploy unless we tell it to.
 *
 * This poller fetches /build-info.json on a slow interval and emits
 * 'build:updated' on window when the commit hash differs from what
 * was bundled. The UI listens and prompts the user to reload — we
 * never reload silently because doing so during active charting could
 * blow away in-progress edits that haven't synced yet.
 */

import { BUILD_INFO } from './build-info';

const POLL_INTERVAL_MS = 60_000; // 1 minute — cheap; the JSON is < 200 bytes
const FIRST_POLL_DELAY_MS = 30_000; // give the SW time to claim before first check

let started = false;

export function startBuildVersionPoller() {
  if (started) return;
  if (typeof window === 'undefined') return;
  // Local dev: __BUILD_INFO__ uses placeholder commit 'dev'. Skip — there's
  // nothing to poll against and the dev server hot-reloads anyway.
  if (BUILD_INFO.commit === 'dev') return;
  started = true;

  const ours = BUILD_INFO.commitFull;

  const check = async () => {
    try {
      // Cache-bust query param — even with NetworkFirst SW route, a Cloudflare
      // edge that ignores Cache-Control could still hand us a stale copy.
      const res = await fetch(`/build-info.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!res.ok) return;
      const remote = (await res.json()) as { commitFull?: string; commit?: string; buildTime?: string };
      if (!remote.commitFull) return;
      if (remote.commitFull !== ours) {
        window.dispatchEvent(
          new CustomEvent('build:updated', {
            detail: {
              fromCommit: ours,
              toCommit: remote.commitFull,
              builtAt: remote.buildTime,
            },
          }),
        );
      }
    } catch {
      // Network blip / offline — try again next interval.
    }
  };

  // First check fires after a short delay (don't race the SW install).
  // After that, every minute. Visibility change retriggers immediately
  // so coming back to the tab from the background catches an update fast.
  setTimeout(check, FIRST_POLL_DELAY_MS);
  setInterval(check, POLL_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
}
